<script setup lang="ts">
import {NotificationSequenceMessage} from "@/notification-sequences/interfaces/NotificationSequence";
import {computed, ref} from "vue";
import {useElementSize} from "@vueuse/core";
import {pluralize} from "@/core/filters/Pluralize";

const props = defineProps<{
    messages: NotificationSequenceMessage[]
}>();

/** Minimum width a step needs to stay readable (badge + gap + truncated title + connector). */
const STEP_MIN_WIDTH = 190;

/** Width reserved for the "+ N messages" chip when some steps are collapsed. */
const OVERFLOW_CHIP_WIDTH = 130;

const container = ref<HTMLElement | null>(null);
const {width: containerWidth} = useElementSize(container);

/** The messages ordered by sending day. */
const orderedMessages = computed(() => {
    return [...props.messages].sort((a, b) => a.delay - b.delay);
});

/** How many steps fit in the container while staying readable. */
const fittingCount = computed(() => {
    if (!containerWidth.value || Math.floor(containerWidth.value / STEP_MIN_WIDTH) >= orderedMessages.value.length) {
        return orderedMessages.value.length;
    }

    return Math.max(1, Math.floor((containerWidth.value - OVERFLOW_CHIP_WIDTH) / STEP_MIN_WIDTH));
});

/** The steps shown in the timeline. */
const visibleSteps = computed(() => orderedMessages.value.slice(0, fittingCount.value));

/** The number of steps collapsed in the "+ N messages" chip. */
const overflowCount = computed(() => orderedMessages.value.length - visibleSteps.value.length);
</script>

<template>
  <div v-if="visibleSteps.length" ref="container" class="flex items-center">
    <div
      v-for="(step, index) in visibleSteps"
      :key="step.id"
      class="flex items-center flex-1 min-w-0"
    >
      <div class="flex items-center gap-2 min-w-0">
        <div
          class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 truncate"
          :class="'bg-white border-2 border-primary text-primary'"
        >
          J{{ step.delay }}
        </div>
        <span
          class="text-sm font-medium truncate text-gray-dark"
        >
          {{ step.title }}
        </span>
      </div>
      <div
        v-if="index < visibleSteps.length - 1 || overflowCount > 0"
        class="flex-1 h-0.5 bg-secondary mx-3 min-w-3"
      />
    </div>
    <span
      v-if="overflowCount > 0"
      class="shrink-0 bg-white border-2 border-secondary text-primary text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap"
    >
      + {{ overflowCount }} {{ pluralize('message', overflowCount) }}
    </span>
  </div>
</template>
